"use client"

import { RetrieveQuotePopup } from "@/shared/components/RetrieveQuotePopup/retrieve-quote-popup"
import { SkeletonPage } from "@dalmatech/ui"
import { Providers } from "@/shared/components/providers"
import { useMounted } from "@/shared/hooks/use-mounted"
import { formatQuoteResponse } from "@/shared/hooks/use-quote-fetching"
import { useRetrieveQuote } from "@/shared/hooks/use-retrieve-quote"
import { ROUTES, ROUTE_PREFIXES } from "@/shared/routing/routes"
import { expireCheckoutSession, getQuote } from "@/shared/http"
import { QuoteLayout } from "@/shared/layouts/quote-layout"
import { computeRedirectRoute } from "@/shared/utils/compute-redirect-route"
import { deleteQuoteIdCookie, saveQuoteIdCookie } from "@/shared/utils/quote-cookie"
import reportAccessibility from "@/shared/utils/reportAccessibility"
import {
  getLiabilityState,
  getQuoteIdFromStorage,
  removeQuoteIdFromStorage,
  storeQuoteId,
  storeUserId,
} from "@/shared/utils/storage"
import { applyStoredLiability } from "@/modules/Pricing/helpers/rc-pricing"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { type Route } from "next"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { usePostHog } from "posthog-js/react"
import React, { useEffect, type PropsWithChildren } from "react"

const Layout = ({ children }: PropsWithChildren) => {
  const isMounted = useMounted()
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [state, send] = useActor(newSubscriptionFlowService)
  const posthog = usePostHog()
  const { showPopup, isCheckingCookie, petNames, quoteId, quoteResponse, dismiss } =
    useRetrieveQuote()

  // Restore per-dog liability selection from sessionStorage after a page refresh
  useEffect(() => {
    if (!isMounted) return
    // Already restored/selected in memory if any dog carries a liability flag.
    if ((state.context.quote?.pets || []).some((p) => p.liability !== undefined)) return
    const stored = getLiabilityState()
    if (!stored) return
    Object.entries(stored).forEach(([idx, petState]) => {
      const petIdx = Number(idx)
      send({
        type: "SET_RC_DATA",
        petIdx,
        rcPrice: petState.price,
        rcPreviouslyInsured: petState.previouslyInsured,
        rcClaimsCount: petState.claimsCount,
      })
      send({ type: "SET_RC_SELECTED", petIdx, rcSelected: petState.selected })
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted])

  useEffect(() => {
    if (!isMounted || isCheckingCookie || showPopup) {
      return
    }
    const quoteIdFromStorage = getQuoteIdFromStorage()
    const quoteIdFromUrl = searchParams.get("quote_id")

    if (!state.context.quote?.meta?.quote_id && quoteIdFromStorage && pathname.includes(ROUTES.recap.path)) {
      const fetchQuote = async () => {
        try {
          await expireCheckoutSession(quoteIdFromStorage)
          const quote = await getQuote(quoteIdFromStorage)
          const formattedQuote = formatQuoteResponse(
            quote,
            undefined,
            undefined,
            state.context.quote?.meta?.user_id
          )
          storeUserId(formattedQuote.meta?.user_id)
          // The RC add-on lives in the liability bundle leg: it's mirrored onto
          // the pets by formatQuoteResponse (backend), with a sessionStorage
          // fallback for same-session reloads before the backend enrichment.
          formattedQuote.pets = applyStoredLiability(formattedQuote.pets)
          send({
            type: "UPDATE_QUOTE",
            quote: formattedQuote,
          })
          if (quote.bundle_id) {
            send({
              type: "SET_BUNDLE_DATA",
              bundle_id: quote.bundle_id,
              liability_quote_id: quote.liability_quote_id ?? null,
            })
          }
        } catch {
          router.push(ROUTES.petName.path)
        }
      }
      void fetchQuote()
    } else if (
      !Boolean(state.context.quote?.pets?.length) &&
      !pathname.includes(ROUTES.recap.path) &&
      !pathname.includes(ROUTES.payment.path) &&
      !pathname.includes(ROUTE_PREFIXES.redirect) &&
      !quoteIdFromUrl &&
      pathname !== ROUTES.petName.path
    ) {
      removeQuoteIdFromStorage()
      router.push(ROUTES.petName.path)
    }
  }, [isMounted, pathname, state, searchParams, isCheckingCookie, showPopup, router, send])

  const handleStartNew = () => {
    posthog.capture("retrieve_quote_decision", { has_retrieved: false, quote_id: quoteId })
    deleteQuoteIdCookie()
    dismiss()
  }

  const handleRetrieve = () => {
    if (!quoteResponse || !quoteId) return
    posthog.capture("retrieve_quote_decision", { has_retrieved: true, quote_id: quoteId })
    const formattedQuote = formatQuoteResponse(quoteResponse)
    storeQuoteId(quoteId)
    storeUserId(formattedQuote.meta?.user_id)
    saveQuoteIdCookie(quoteId)
    send({ type: "UPDATE_QUOTE", quote: formattedQuote })

    // The RC add-on is restored from the liability bundle leg (backend) via
    // formatQuoteResponse; re-link the bundle so the bundled checkout works.
    if (quoteResponse.bundle_id) {
      send({
        type: "SET_BUNDLE_DATA",
        bundle_id: quoteResponse.bundle_id,
        liability_quote_id: quoteResponse.liability_quote_id ?? null,
      })
    }

    // Fallback for same-session retrieval before the backend enrichment is live:
    // restore the per-dog selection from sessionStorage only if the backend
    // didn't already provide it.
    const backendProvidesLiability = (formattedQuote.pets ?? []).some(
      (p) => p.liability !== undefined
    )
    const storedLiability = getLiabilityState()
    if (!backendProvidesLiability && storedLiability) {
      Object.entries(storedLiability).forEach(([idx, petState]) => {
        const petIdx = Number(idx)
        send({
          type: "SET_RC_DATA",
          petIdx,
          rcPrice: petState.price,
          rcPreviouslyInsured: petState.previouslyInsured,
          rcClaimsCount: petState.claimsCount,
        })
        send({ type: "SET_RC_SELECTED", petIdx, rcSelected: petState.selected })
      })
    }

    const redirectRoute = computeRedirectRoute(quoteResponse)

    // Set current pet: for /prepackages, target the first pet without a formula
    const petWithoutFormula = formattedQuote.pets?.find((p) => !p.pricing)
    const targetPet =
      redirectRoute === ROUTES.prepackages.path && petWithoutFormula
        ? petWithoutFormula
        : formattedQuote.pets?.[0]
    if (targetPet) {
      send({ type: "SET_CURRENT_PET_IDX", petId: targetPet.id, petIndex: targetPet.idx })
    }

    dismiss()
    router.push(redirectRoute as Route)
  }

  const isReady = isMounted && !isCheckingCookie && !showPopup

  return (
    <Providers>
      <QuoteLayout>{isReady ? children : <SkeletonPage variant="narrow" />}</QuoteLayout>
      <RetrieveQuotePopup
        open={showPopup}
        petNames={petNames}
        onStartNew={handleStartNew}
        onRetrieve={handleRetrieve}
      />
    </Providers>
  )
}

reportAccessibility(React).catch((error) => {
  console.log("Error while loading Axe:", error)
})

export default Layout
